/**
 * Local-time helpers for rescheduling staged marketing posts from the UI.
 * Everything here works in the browser's local timezone, which is what the
 * calendar grid and the post modals display.
 */

export function localDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** "HH:MM" (24h) for a Date in local time. */
export function localClock(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Build a local Date from a YYYY-MM-DD key and an HH:MM clock. */
export function combineLocal(dateKey: string, clock: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  const c = /^(\d{1,2}):(\d{2})$/.exec(clock);
  if (!m || !c) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(c[1]), Number(c[2]), 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Keep the clock from `iso` and move it onto `dateKey` (local).
 * Returns null when either input is invalid.
 */
export function moveIsoToDate(iso: string, dateKey: string): Date | null {
  const src = new Date(iso);
  if (Number.isNaN(src.getTime())) return null;
  return combineLocal(dateKey, localClock(src));
}

/** Earliest clock allowed today (next whole minute) as HH:MM. */
export function nextMinuteClock(now = new Date()): string {
  const d = new Date(now.getTime());
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() + 1);
  return localClock(d);
}

export function isSameLocalDay(a: Date, b: Date): boolean {
  return localDateKey(a) === localDateKey(b);
}

export function formatScheduleDate(d: Date): string {
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatScheduleTime(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
